import React, { useState, useEffect, useCallback } from "react";
import {
  Calendar,
  Clock,
  MapPin,
  Plus,
  Search,
  ExternalLink,
} from "lucide-react";
import {
  governanceMeetingsService,
  GovernanceMeeting,
} from "../../services/governanceService";
import MeetingDetailModal from "./MeetingDetailModal";
import FormControl from "../common/FormControl";
import Button from "../common/Button";
import Badge from "../common/Badge";
import Pagination from "../common/Pagination";
import Toast, { ToastMessage } from "../common/Toast";
import { statusVariant } from "../../utils/statusVariant";
// v2 R23/R28: a single 3-step wizard handles both create and edit, replacing
// the legacy inline create-form + the legacy edit modal flow that used to
// share that form.
import CreateMeetingWizard from "./CreateMeetingWizard";
import NoDataFound from "../common/NoDataFound";
import Skeleton from "../common/Skeleton";
import { usePermission, NOT_AUTHORISED_MSG } from "../../contexts/PermissionContext";
import { useEntitySettings } from "../../contexts/EntitySettingsContext";

type MeetingTypeKey =
  | "Board"
  | "Committee"
  | "Review"
  | "Adhoc"
  | "Investigation"
  | "Remediation"
  | "CAPA"
  | "Statutory";

const MEETING_TYPE_OPTIONS: { value: MeetingTypeKey | ""; label: string }[] = [
  { value: "", label: "All Types" },
  { value: "Board", label: "Board" },
  { value: "Committee", label: "Committee" },
  { value: "Review", label: "Review" },
  { value: "Adhoc", label: "Adhoc" },
  { value: "Investigation", label: "Investigation" },
  { value: "Remediation", label: "Remediation" },
  { value: "CAPA", label: "CAPA" },
  { value: "Statutory", label: "Statutory" },
];


const formatTime = (t?: string | null) => {
  if (!t) return "";
  const parts = t.split(":");
  if (parts.length >= 2) {
    const h = parseInt(parts[0]);
    const m = parts[1];
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${m} ${ampm}`;
  }
  return t;
};

const GovernanceMeetingsNetBody = () => {
  const { allowed: canModify, loading: permLoading } = usePermission('GOV_MEETINGS', 'view+modify');
  const { allowed: canView, loading: viewLoading } = usePermission('GOV_MEETINGS', 'view');
  const { formatDate, formatDateTime } = useEntitySettings();
  const [meetings, setMeetings] = useState<GovernanceMeeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<MeetingTypeKey | "">("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // v2 R23/R28: wizard owns both create and edit flows. We keep a single
  // open-flag + a mode discriminant; the meeting being edited lives in
  // wizardMeeting (null for create).
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState<"create" | "edit">("create");
  const [wizardMeeting, setWizardMeeting] =
    useState<GovernanceMeeting | null>(null);
  const [detailMeeting, setDetailMeeting] = useState<GovernanceMeeting | null>(
    null,
  );
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const loadMeetings = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      const data = await governanceMeetingsService.getAll({
        meeting_type: typeFilter || undefined,
        meeting_status: statusFilter || undefined,
      });
      setMeetings(data);
    } catch (err: any) {
      setToast({ type: "error", message: err.message || "Failed to load meetings" });
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, canView]);

  useEffect(() => {
    loadMeetings();
  }, [loadMeetings]);

  useEffect(() => {
    setPage(1);
  }, [typeFilter, searchTerm, statusFilter, pageSize]);

  const filteredMeetings = meetings.filter((m) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      (m.meeting_title || "").toLowerCase().includes(q) ||
      (m.meeting_code || "").toLowerCase().includes(q)
    );
  });

  const totalRecords = filteredMeetings.length;
  const paginatedMeetings = filteredMeetings.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const handleAddNew = () => {
    setWizardMode("create");
    setWizardMeeting(null);
    setWizardOpen(true);
  };

  const handleEdit = (meeting: GovernanceMeeting) => {
    setWizardMode("edit");
    setWizardMeeting(meeting);
    setWizardOpen(true);
  };

  const handleWizardSaved = (saved: GovernanceMeeting) => {
    setToast({
      type: "success",
      message:
        wizardMode === "edit"
          ? "Meeting updated successfully"
          : "Meeting created successfully",
    });
    loadMeetings();
    // Keep detailMeeting in sync if the user was editing the same meeting
    // that's currently open in the detail modal.
    if (detailMeeting && detailMeeting.id === saved.id) {
      setDetailMeeting(saved);
    }
  };

  if (!viewLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to meetings." />
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── Full-width card: toolbar + table (left rail removed per SPEC R1) ── */}
      <div className="netbody-card flex flex-col flex-1 min-w-0">
        {/* Toolbar */}
        <div className="netbody-header">
          <div className="flex items-center gap-2 ml-auto">
            <FormControl.SearchSelect
              value={typeFilter}
              onChange={(v) => setTypeFilter((v ?? '') as MeetingTypeKey | '')}
              className="filter-status"
              aria-label="Meeting type filter"
              data-testid="SEL_meeting_type_filter"
              noSearchIcon
              options={MEETING_TYPE_OPTIONS}
            />
            <FormControl.SearchSelect
              value={statusFilter}
              onChange={(v) => setStatusFilter(String(v ?? ''))}
              className="filter-status"
              aria-label="Meeting status filter"
              data-testid="SEL_meeting_status_filter"
              noSearchIcon
              options={[
                { value: '', label: 'All Status' },
                { value: 'DRAFT', label: 'Draft' },
                { value: 'SCHEDULED', label: 'Scheduled' },
                { value: 'IN_PROGRESS', label: 'In Progress' },
                { value: 'CLOSED', label: 'Closed' },
                { value: 'CANCELLED', label: 'Cancelled' },
              ]}
            />
            <FormControl.Input
              type="text"
              placeholder="Search meetings…"
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
              leadingIcon={<Search size={14} />}
              className="filter-search"
              data-testid="INP_meeting_search"
            />
            <Button variant="fill" icon={Plus} onClick={handleAddNew} disabled={permLoading || !canModify} title={!canModify ? NOT_AUTHORISED_MSG : undefined} data-testid="BTN_meeting_create">
              Create
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="netbody-content">
          <div className="scroll-container flex-1 overflow-hidden">
            <div className="table-list">
              <div className="table-list-scroll">
                <table className="table-list-table">
                  <thead>
                    <tr>
                      <th className="table-list-th table-list-cell--sno">
                        S.No
                      </th>
                      <th className="table-list-th">Meeting Title</th>
                      <th className="table-list-th">Type</th>
                      <th className="table-list-th">Date & Time</th>
                      <th className="table-list-th">Location</th>
                      <th className="table-list-th table-list-cell--status">Status</th>
                      <th className="table-list-th">Join</th>
                    </tr>
                  </thead>
                  <tbody aria-busy={loading} aria-live="polite">
                    {loading && <Skeleton.TableRows cols={['3xs', 'md', 'xs', 'xs', 'xs', 'pill', '3xs']} />}
                    {!loading && filteredMeetings.length === 0 && (
                      <tr>
                        <td colSpan={7} className="table-list-cell">
                          <NoDataFound title="No Meetings Found" message='Click "Create" to schedule one.' />
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      paginatedMeetings.map((meeting, idx) => (
                        <tr
                          key={meeting.id}
                          className="table-list-row"
                          onClick={() => setDetailMeeting(meeting)}
                          data-testid={`BTN_meeting_row_${meeting.id}_open`}
                        >
                          <td className="table-list-cell table-list-cell--sno">
                            {(page - 1) * pageSize + idx + 1}
                          </td>
                          <td className="table-list-cell">
                            <span className="table-list-cell-title">
                              {meeting.meeting_title}
                            </span>
                            {meeting.meeting_description && (
                              <span className="table-list-cell-sub">
                                {meeting.meeting_description.substring(0, 60)}
                                {meeting.meeting_description.length > 60
                                  ? "…"
                                  : ""}
                              </span>
                            )}
                          </td>
                          <td className="table-list-cell">
                            {meeting.meeting_type}
                          </td>
                          <td className="table-list-cell">
                            <div className="flex items-center gap-1 text-xs">
                              <Calendar size={11} className="text-gray-400" />
                              <span>
                                {formatDate(meeting.scheduled_date)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-400">
                              <Clock size={11} />
                              <span>
                                {formatTime(meeting.scheduled_time_from)}
                                {meeting.scheduled_time_to
                                  ? ` – ${formatTime(meeting.scheduled_time_to)}`
                                  : ""}
                              </span>
                            </div>
                          </td>
                          <td className="table-list-cell">
                            {meeting.location ? (
                              <div className="flex items-center gap-1 text-xs">
                                <MapPin
                                  size={11}
                                  className="text-gray-400 shrink-0"
                                />
                                <span
                                  title={meeting.location}
                                  className="truncate max-w-[120px]"
                                >
                                  {meeting.location}
                                </span>
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="table-list-cell">
                            <Badge variant={statusVariant(meeting.meeting_status)} size="sm">
                              {(meeting.meeting_status || "").replace("_", " ")}
                            </Badge>
                          </td>
                          <td
                            className="table-list-cell"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {meeting.meeting_link ? (
                              <a
                                href={meeting.meeting_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="meeting-join-link"
                                title={meeting.meeting_link}
                                data-testid={`LNK_meeting_row_${meeting.id}_join`}
                              >
                                <ExternalLink size={11} />
                                <span>Join</span>
                              </a>
                            ) : (
                              <span className="text-xs text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={page}
                pageSize={pageSize}
                totalRecords={totalRecords}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                recordLabel="meetings"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Meeting Detail Modal — opened from GRC admin /governance route, so
          viewMode='admin' (NO voting controls, BUT full edit + voting results +
          Create Task per latest UAT). Staff portal opens the same modal with
          viewMode='participant' (Attend → Vote, no edits). */}
      {detailMeeting && (
        <MeetingDetailModal
          meeting={detailMeeting}
          onClose={() => setDetailMeeting(null)}
          onMeetingUpdated={loadMeetings}
          onEdit={() => handleEdit(detailMeeting)}
          viewMode="admin"
        />
      )}

      {/* v2 R23/R28: 3-step wizard handles both create and edit. The legacy
          single-form modal + the separate edit handler have been deleted —
          mode is the single discriminant. */}
      <CreateMeetingWizard
        mode={wizardMode}
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSaved={handleWizardSaved}
        meeting={wizardMode === "edit" ? wizardMeeting || undefined : undefined}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default GovernanceMeetingsNetBody;
